"use client";
import clsx from "clsx";
import dayjs from "dayjs";
import { useState } from "react";
import { type DateAfter, type DateBefore, DayPicker, type Matcher, type OnSelectHandler } from "react-day-picker";
import { fr } from "react-day-picker/locale";
import "react-day-picker/style.css";
import "./highlights.css";
import { type Control, type FieldValues, type Path, useController } from "react-hook-form";

declare module "react" {
  interface CSSProperties {
    [key: `--${string}`]: string | number;
  }
}

type Props<T extends FieldValues> = {
  label: string;
  name: Path<T>;
  control: Control<T>;
  className?: string;
  allYears?: boolean;
  placeholder?: string;
  defaultValue?: string;
  highlights?: { start: string; end: string }[];
  travelDatesRange?: { start: string; end: string };
  selectionRange?: { start: string; end: string };
  type?: "depart" | "retour";
  after?: string;
  before?: string;
  currentYear?: number;
  toYear?: number;
  fromYear?: number;
  fromMonthNumber?: number;
  toMonthNumber?: number;
};
export const DayInput = <T extends FieldValues>({
  label,
  placeholder,
  control,
  className,
  name,
  allYears = false,
  defaultValue,
  highlights,
  travelDatesRange,
  selectionRange,
  type,
  after,
  before,
  currentYear = new Date().getFullYear(),
  toYear = 20,
  fromYear = 0,
  fromMonthNumber = 0,
  toMonthNumber = 11,
}: Props<T>) => {
  const {
    field: { onChange, value },
    fieldState: { error },
  } = useController({ name, control });

  const [isOpen, setIsOpen] = useState(false);

  const normalizeRange = (range?: { start: string; end: string }) => {
    if (!range) {
      return undefined;
    }
    const start = dayjs(range.start);
    const end = dayjs(range.end);
    if (!start.isValid() || !end.isValid()) {
      return undefined;
    }
    if (start.isAfter(end)) {
      return { from: end.toDate(), to: start.toDate() };
    }
    return { from: start.toDate(), to: end.toDate() };
  };

  const fromMonth = allYears ? new Date(currentYear - 110, 0) : new Date(currentYear - fromYear, fromMonthNumber);
  const toMonth = allYears ? new Date(currentYear, 11) : new Date(currentYear + toYear, toMonthNumber);

  const handleSelect: OnSelectHandler<Date> = (date) => {
    if (!date) {
      return;
    }
    onChange(date.toISOString());
    setIsOpen(false);
  };

  const beforeDate: DateBefore | undefined = !!before ? { before: dayjs(before).toDate() } : undefined;
  const afterDate: DateAfter | undefined = !!after ? { after: dayjs(after).toDate() } : undefined;

  const matchers: Matcher[] = [beforeDate, afterDate].filter((m) => m !== undefined);

  const selectionRangeMatcher = normalizeRange(selectionRange);
  const travelRangeMatcher = normalizeRange(travelDatesRange);
  const disabledMatchers: Matcher[] = [...matchers];
  if (selectionRangeMatcher && travelRangeMatcher) {
    if (type === "depart") {
      disabledMatchers.push(
        { before: selectionRangeMatcher.from },
        { after: dayjs(travelRangeMatcher.from).subtract(1, "day").toDate() },
      );
    } else {
      disabledMatchers.push(
        {
          before: dayjs(travelRangeMatcher.from).subtract(1, "day").toDate(),
        },
        { after: dayjs(selectionRangeMatcher.to).toDate() },
      );
    }
  }

  return (
    <div className={clsx("relative cursor-pointer", className)} onClick={() => setIsOpen(!isOpen)}>
      <div
        className={clsx("text-base font-bold", {
          "text-[#C92A2A]": error,
          "text-[#22201F]": !error,
        })}
      >
        {label}
      </div>
      <div
        className={clsx(
          "mt-2 bg-white flex justify-between items-center rounded-lg border py-3 px-4 w-full text-base font-normal placeholder:text-[#B8B8B8]",
          {
            "text-[#C92A2A] border-[#C92A2A]": error,
            "text-[#22201F] border-[#E5E5E5]": !error,
          },
        )}
      >
        <p className="h-6">{value ? dayjs(value).format("DD/MM/YYYY") : placeholder}</p>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          className="text-tx-2"
        >
          <path
            d="M12.6667 2.6665H3.33333C2.59695 2.6665 2 3.26346 2 3.99984V13.3332C2 14.0696 2.59695 14.6665 3.33333 14.6665H12.6667C13.403 14.6665 14 14.0696 14 13.3332V3.99984C14 3.26346 13.403 2.6665 12.6667 2.6665Z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M10.6667 1.3335V4.00016"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M5.33325 1.3335V4.00016"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path d="M2 6.6665H14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
      {error?.message && <p className="text-[#C92A2A] text-xs leading-5 mt-[5px]">{error.message}</p>}
      {isOpen && (
        <div
          onClick={(e) => e.stopPropagation()}
          className="absolute bg-white rounded-sm shadow-sm drop-shadow-sm border border-[#E5E5E5] z-10 top-6 left-0"
        >
          <DayPicker
            className="m-4"
            style={{
              "--rdp-accent-color": "#22201F",
            }}
            modifiers={{
              ...(selectionRangeMatcher ? { selectionRange: selectionRangeMatcher } : {}),
              ...(selectionRangeMatcher ? { firstSelectable: selectionRangeMatcher.from } : {}),
              ...(selectionRangeMatcher ? { lastSelectable: selectionRangeMatcher.to } : {}),
              ...(travelRangeMatcher ? { travelRange: travelRangeMatcher } : {}),
              ...(travelRangeMatcher ? { firstTravel: travelRangeMatcher.from } : {}),
              ...(travelRangeMatcher ? { lastTravel: travelRangeMatcher.to } : {}),
              ...(travelRangeMatcher && selectionRangeMatcher
                ? type === "depart"
                  ? {
                      others: {
                        after: travelRangeMatcher.to,
                        before: selectionRangeMatcher.from,
                      },
                    }
                  : {
                      others: {
                        after: selectionRangeMatcher.to,
                        before: travelRangeMatcher.from,
                      },
                    }
                : {}),
            }}
            modifiersClassNames={{
              others: "not-selectable",
              ...(type === "depart" ? { firstSelectable: "range-selectable-first" } : {}),
              selectionRange: "range-selectable",
              lastSelectable: "range-selectable-last",
              firstTravel: type === "depart" ? "range-travel-first-pre" : "range-travel-first",
              travelRange: "range-travel",
              lastTravel: type === "retour" ? "range-travel-last-post" : "range-travel-last",
            }}
            mode="single"
            selected={value ? dayjs(value).toDate() : defaultValue ? dayjs(defaultValue).toDate() : dayjs().toDate()}
            onSelect={handleSelect}
            locale={fr}
            classNames={{
              weekday: "pt-4 pb-2 text-sm font-normal",
            }}
            defaultMonth={
              value ? dayjs(value).toDate() : defaultValue ? dayjs(defaultValue).toDate() : dayjs().toDate()
            }
            captionLayout="dropdown"
            startMonth={fromMonth}
            endMonth={toMonth}
            hideNavigation
            required
            disabled={disabledMatchers}
          />
        </div>
      )}
    </div>
  );
};
